import { InsoleSide } from "@prisma/client";
import type { BufferGeometry } from "three";
import { STLLoader } from "three-stdlib";

// Fraction of an axis extent treated as "on the flat face" when counting the
// coplanar vertices that identify the sole's base side.
const FLAT_FACE_TOLERANCE = 0.02;
// Length fractions delimiting the heel quartile and the forefoot quartile used
// to decide which end is the toe (the forefoot is the wider end).
const HEEL_QUARTILE = 0.25;
const FOREFOOT_QUARTILE = 0.75;
// Length band kept as the midfoot when locating the arch. It excludes the heel
// cup and the toe spring, where both edges rise on a normal insole.
const MIDFOOT_LOW = 0.3;
const MIDFOOT_HIGH = 0.7;
// Single global calibration switch. Every model is classified in the same
// canonical frame, so the arch always falls on the same width side for a given
// foot; if the whole library comes out mirrored, flip this one boolean.
const ARCH_ON_POSITIVE_WIDTH_IS_LEFT = true;

// Detect the handedness of an insole mesh. Handedness is a chirality, so it is
// resolved in a canonical frame rebuilt from the geometry itself:
//  - axes come from the bounding box (height = thinnest, length = longest),
//  - the up direction points from the flat sole base toward the plantar surface,
//  - the toe direction is the wider (forefoot) end of the length axis,
//  - the width direction is the cross product toe x up, NOT the raw axis sign.
// The medial longitudinal arch is the highest part of the plantar surface and
// always sits on the inner (medial) side of the midfoot, so whichever width side
// carries the higher midfoot surface is the medial side and names the foot.
// Because the width direction is derived, the result is invariant to the source
// file's orientation and only flips when the mesh is genuinely mirrored.
export function detectInsoleSide(geometry: BufferGeometry): InsoleSide {
  const pos = geometry.getAttribute("position").array as ArrayLike<number>;
  const count = pos.length / 3;
  if (count === 0) {
    return InsoleSide.LEFT;
  }

  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  const sum = [0, 0, 0];
  for (let i = 0; i < count; i++) {
    for (let a = 0; a < 3; a++) {
      const v = pos[i * 3 + a];
      if (v < min[a]) {
        min[a] = v;
      }
      if (v > max[a]) {
        max[a] = v;
      }
      sum[a] += v;
    }
  }

  const ext = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  const centroid = [sum[0] / count, sum[1] / count, sum[2] / count];

  let upAxis = 0;
  if (ext[1] < ext[upAxis]) {
    upAxis = 1;
  }
  if (ext[2] < ext[upAxis]) {
    upAxis = 2;
  }
  const planar = [0, 1, 2].filter((a) => a !== upAxis);
  const lengthAxis = ext[planar[0]] >= ext[planar[1]] ? planar[0] : planar[1];
  const widthAxis = planar[0] === lengthAxis ? planar[1] : planar[0];

  const tolUp = Math.max(ext[upAxis] * FLAT_FACE_TOLERANCE, 1e-4);
  let minFlat = 0;
  let maxFlat = 0;
  for (let i = 0; i < count; i++) {
    const u = pos[i * 3 + upAxis];
    if (u <= min[upAxis] + tolUp) {
      minFlat++;
    } else if (u >= max[upAxis] - tolUp) {
      maxFlat++;
    }
  }
  const upDir = minFlat >= maxFlat ? 1 : -1;

  const lengthMin = min[lengthAxis];
  const lengthRange = ext[lengthAxis] || 1;
  let heelSpan = 0;
  let toeSpan = 0;
  let heelMin = Infinity;
  let heelMax = -Infinity;
  let foreMin = Infinity;
  let foreMax = -Infinity;
  for (let i = 0; i < count; i++) {
    const l = (pos[i * 3 + lengthAxis] - lengthMin) / lengthRange;
    const w = pos[i * 3 + widthAxis];
    if (l < HEEL_QUARTILE) {
      if (w < heelMin) {
        heelMin = w;
      }
      if (w > heelMax) {
        heelMax = w;
      }
    } else if (l > FOREFOOT_QUARTILE) {
      if (w < foreMin) {
        foreMin = w;
      }
      if (w > foreMax) {
        foreMax = w;
      }
    }
  }
  heelSpan = heelMax - heelMin;
  toeSpan = foreMax - foreMin;
  const toeDir = toeSpan >= heelSpan ? 1 : -1;

  const toeVec = [0, 0, 0];
  toeVec[lengthAxis] = toeDir;
  const upVec = [0, 0, 0];
  upVec[upAxis] = upDir;
  const widthVec = [
    toeVec[1] * upVec[2] - toeVec[2] * upVec[1],
    toeVec[2] * upVec[0] - toeVec[0] * upVec[2],
    toeVec[0] * upVec[1] - toeVec[1] * upVec[0],
  ];

  let posSideHeight = 0;
  let posSideCount = 0;
  let negSideHeight = 0;
  let negSideCount = 0;
  for (let i = 0; i < count; i++) {
    const l = (pos[i * 3 + lengthAxis] - lengthMin) / lengthRange;
    if (l < MIDFOOT_LOW || l > MIDFOOT_HIGH) {
      continue;
    }
    const widthOffset =
      (pos[i * 3] - centroid[0]) * widthVec[0] +
      (pos[i * 3 + 1] - centroid[1]) * widthVec[1] +
      (pos[i * 3 + 2] - centroid[2]) * widthVec[2];
    const height = pos[i * 3 + upAxis] * upDir;
    if (widthOffset >= 0) {
      posSideHeight += height;
      posSideCount++;
    } else {
      negSideHeight += height;
      negSideCount++;
    }
  }
  const posMean = posSideCount > 0 ? posSideHeight / posSideCount : 0;
  const negMean = negSideCount > 0 ? negSideHeight / negSideCount : 0;
  const archOnPositiveWidth = posMean >= negMean;

  if (archOnPositiveWidth) {
    return ARCH_ON_POSITIVE_WIDTH_IS_LEFT ? InsoleSide.LEFT : InsoleSide.RIGHT;
  }
  return ARCH_ON_POSITIVE_WIDTH_IS_LEFT ? InsoleSide.RIGHT : InsoleSide.LEFT;
}

export async function detectInsoleSideFromFile(file: File): Promise<InsoleSide> {
  const buffer = await file.arrayBuffer();
  const geometry = new STLLoader().parse(buffer);
  return detectInsoleSide(geometry);
}
